import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../utils/cn';
import { Label } from './Label';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="space-y-1.5">
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <input
          id={inputId}
          ref={ref}
          className={cn(
            'w-full rounded-lg border px-3 py-2 text-sm',
            'bg-transparent text-text-primary placeholder:text-text-subtle dark:placeholder:text-text-muted',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'disabled:bg-bg-muted disabled:text-text-muted disabled:cursor-not-allowed disabled:border-border-muted',
            error
              ? 'border-feedback-error focus:ring-feedback-error'
              : 'border-border-strong hover:border-border-input-hover',
            className,
          )}
          {...props}
        />
        {error && <p className="text-xs text-feedback-error">{error}</p>}
        {hint && !error && <p className="text-xs text-text-muted">{hint}</p>}
      </div>
    );
  },
);

Input.displayName = 'Input';
